import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';

const ReturnManagement = () => {
    const { authFetch, selectedDepo } = useContext(AppContext);
    const { t } = useTranslation();
    const [returns, setReturns] = useState([]);
    
    // Filters
    const [filters, setFilters] = useState({
        invoice_number: '',
        start_date: '',
        end_date: '',
        customer_id: 'All'
    });

    const [customers, setCustomers] = useState([]);
    const [isProcessOpen, setIsProcessOpen] = useState(false);
    
    // Process Return State
    const [searchInvoice, setSearchInvoice] = useState('');
    const [invoiceData, setInvoiceData] = useState(null);
    const [returnItems, setReturnItems] = useState([]); // [{id, product_name, quantity, price_ttc, return_qty}]
    const [returnReason, setReturnReason] = useState('');
    
    // View Return State
    const [viewedReturn, setViewedReturn] = useState(null);
    const [viewedReturnItems, setViewedReturnItems] = useState([]);
    const [isViewLoading, setIsViewLoading] = useState(false);
    
    useEffect(() => {
        authFetch('/api/customers').then(r => r.json()).then(d => setCustomers(Array.isArray(d) ? d : []));
    }, [authFetch]);

    useEffect(() => {
        fetchAll();
    }, [filters, authFetch]);

    const fetchAll = () => {
        // Fetch historical returns
        let url = '/api/refunds';
        authFetch(url).then(r => r.json()).then(d => setReturns(Array.isArray(d) ? d : []));
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const fetchInvoiceForReturn = async () => {
        if (!searchInvoice) return;
        try {
            const res = await authFetch(`/api/invoices?invoice_number=${searchInvoice}`);
            const data = await res.json();
            
            // If returns an array, find exact match
            const inv = Array.isArray(data) ? data.find(i => i.invoice_number === searchInvoice) : data;
            
            if (inv && inv.id) {
                setInvoiceData(inv);
                setReturnItems((inv.items || []).map(it => ({
                    id: it.id,
                    product_name: it.product_name,
                    max_qty: it.quantity,
                    price_ttc: parseFloat(it.price_ttc || 0),
                    return_qty: 0
                })));
            } else {
                alert(t('Invoice not found'));
                setInvoiceData(null);
                setReturnItems([]);
            }
        } catch (err) {
            alert(t('Error fetching invoice'));
        }
    };

    const handleProcessReturn = async (e) => {
        e.preventDefault();
        const itemsToRefund = returnItems.filter(it => it.return_qty > 0);
        
        if (itemsToRefund.length === 0) {
            alert(t('Please select at least one item to return.'));
            return;
        }

        if (!returnReason) {
            alert(t('Please provide a reason for the return.'));
            return;
        }

        try {
            const res = await authFetch('/api/refunds', {
                method: 'POST',
                body: JSON.stringify({
                    invoice_id: invoiceData.id,
                    type: 'partial',
                    items: itemsToRefund.map(it => ({
                        invoice_item_id: it.id,
                        qty_refunded: it.return_qty
                    })),
                    reason: returnReason
                })
            });
            const data = await res.json();
            if (res.ok) {
                alert(t('Return processed successfully!'));
                setIsProcessOpen(false);
                setInvoiceData(null);
                setReturnReason('');
                fetchAll();
            } else {
                alert(data.message || t('Return failed'));
            }
        } catch (err) {
            alert(t('Network error'));
        }
    };

    const updateReturnQty = (index, value) => {
        const val = parseFloat(value) || 0;
        const items = [...returnItems];
        if (val >= 0 && val <= items[index].max_qty) {
            items[index].return_qty = val;
            setReturnItems(items);
        }
    };

    const columns = [
        { header: t('Date'), accessor: 'created_at', render: r => new Date(r.created_at).toLocaleString() },
        { header: t('Invoice No'), accessor: 'invoice_number', render: r => <span className="font-mono text-gray-500 font-bold">{r.invoice_number}</span> },
        { header: t('Customer'), accessor: 'customer_name', render: r => r.customer_name || t('Walk-in Customer') },
        { header: t('Refund Amount'), accessor: 'total_refund', render: r => <span className="font-bold text-red-600">CFA{parseFloat(r.total_refund).toFixed(2)}</span> },
        { header: t('Reason'), accessor: 'reason' },
        { header: t('Processed By'), accessor: 'processed_by_name', render: r => r.processed_by_name || 'System' },
        { 
            header: t('Action'), 
            accessor: 'id', 
            render: r => (
                <button 
                    onClick={() => handleViewReturn(r)}
                    className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-md text-xs font-bold transition-colors"
                >
                    {t('View Items')}
                </button>
            )
        }
    ];

    const handleViewReturn = async (ret) => {
        setViewedReturn(ret);
        setViewedReturnItems([]);
        setIsViewLoading(true);
        try {
            const res = await authFetch(`/api/refunds?invoice_id=${ret.invoice_id}`);
            const data = await res.json();
            if (Array.isArray(data)) {
                // Filter items that belong to this specific refund ID
                const items = data.filter(d => d.id === ret.id);
                setViewedReturnItems(items);
            }
        } catch (err) {
            console.error('Failed to fetch return details', err);
        } finally {
            setIsViewLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <header className="flex justify-between items-center">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">{t('Return Management')}</h2>
                    <p className="text-gray-500 mt-1">{t('Process returns and view return history.')}</p>
                </div>
                <button onClick={() => setIsProcessOpen(true)} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded shadow flex items-center gap-2 font-bold transition-all transform hover:scale-105">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    {t('Process Return')}
                </button>
            </header>

            {/* Historical Returns List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <DataTable columns={columns} data={returns} />
            </div>

            {/* Process Return Modal */}
            <Modal isOpen={isProcessOpen} onClose={() => { setIsProcessOpen(false); setInvoiceData(null); }} title={t('Process New Return')} size="max-w-4xl">
                <div className="space-y-6">
                    {/* Invoice Search */}
                    <div className="flex gap-2 items-end">
                        <div className="flex-1">
                            <label className="block text-sm font-semibold text-gray-700 mb-1">{t('Find Invoice Number')}</label>
                            <input 
                                type="text" 
                                value={searchInvoice} 
                                onChange={e => setSearchInvoice(e.target.value)} 
                                className="w-full border p-2 rounded" 
                                placeholder="INV-0001"
                            />
                        </div>
                        <button onClick={fetchInvoiceForReturn} className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 font-bold">{t('Fetch')}</button>
                    </div>

                    {invoiceData && (
                        <form onSubmit={handleProcessReturn} className="space-y-6">
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                    <span className="block text-gray-500 font-semibold">{t('Customer')}</span>
                                    <span className="font-bold text-gray-800">{invoiceData.customer_name || t('Walk-in')}</span>
                                </div>
                                <div>
                                    <span className="block text-gray-500 font-semibold">{t('Invoice Total')}</span>
                                    <span className="font-bold text-gray-800">CFA{parseFloat(invoiceData.total_ttc).toFixed(2)}</span>
                                </div>
                                <div>
                                    <span className="block text-gray-500 font-semibold">{t('Paid Amount')}</span>
                                    <span className="font-bold text-emerald-600">CFA{parseFloat(invoiceData.amount_paid).toFixed(2)}</span>
                                </div>
                                <div>
                                    <span className="block text-gray-500 font-semibold">{t('Status')}</span>
                                    <span className="font-bold text-gray-800">{t(invoiceData.status)}</span>
                                </div>
                            </div>

                            <div>
                                <h3 className="font-bold text-gray-800 mb-2">{t('Select Items to Return')}</h3>
                                <div className="border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="w-full overflow-x-auto max-w-full">
<table className="w-full text-left text-sm">
                                        <thead className="bg-gray-100 text-gray-600">
                                            <tr>
                                                <th className="p-3">{t('Product')}</th>
                                                <th className="p-3">{t('Purchased Qty')}</th>
                                                <th className="p-3">{t('Unit Price (TTC)')}</th>
                                                <th className="p-3 w-32">{t('Return Qty')}</th>
                                                <th className="p-3 text-right">{t('Refund Value')}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {returnItems.map((item, idx) => {
                                                const unitPrice = item.max_qty > 0 ? item.price_ttc / item.max_qty : 0;
                                                const refundValue = unitPrice * item.return_qty;
                                                return (
                                                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                                        <td className="p-3">{item.product_name}</td>
                                                        <td className="p-3">{item.max_qty}</td>
                                                        <td className="p-3">CFA{unitPrice.toFixed(2)}</td>
                                                        <td className="p-3">
                                                            <input 
                                                                type="number" 
                                                                min="0" 
                                                                max={item.max_qty} 
                                                                value={item.return_qty === 0 ? '' : item.return_qty}
                                                                onChange={e => updateReturnQty(idx, e.target.value)}
                                                                className="w-full border p-1 rounded focus:ring-indigo-500"
                                                                placeholder="0"
                                                            />
                                                        </td>
                                                        <td className="p-3 text-right font-bold text-red-600">CFA{refundValue.toFixed(2)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
</div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">{t('Reason for Return')}</label>
                                <textarea 
                                    required
                                    className="w-full border p-2 rounded focus:ring-indigo-500" 
                                    rows="2"
                                    value={returnReason}
                                    onChange={e => setReturnReason(e.target.value)}
                                    placeholder={t('e.g. Defective product, customer changed mind...')}
                                ></textarea>
                            </div>

                            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                                <div className="text-sm text-gray-500">
                                    {t('Total Refund Amount:')} 
                                    <span className="text-xl font-black text-red-600 ml-2">
                                        CFA{returnItems.reduce((acc, it) => acc + (it.max_qty > 0 ? (it.price_ttc / it.max_qty) * it.return_qty : 0), 0).toFixed(2)}
                                    </span>
                                </div>
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => setIsProcessOpen(false)} className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded font-semibold transition-colors">{t('Cancel')}</button>
                                    <button type="submit" className="px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-bold shadow transition-colors">{t('Confirm Return & Restock')}</button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>
            </Modal>

            {/* View Return Details Modal */}
            <Modal isOpen={!!viewedReturn} onClose={() => setViewedReturn(null)} title={t('Return Details')} size="max-w-3xl">
                {viewedReturn && (
                    <div className="space-y-6">
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                                <span className="block text-gray-500 font-semibold">{t('Invoice No')}</span>
                                <span className="font-bold text-gray-800 font-mono">{viewedReturn.invoice_number}</span>
                            </div>
                            <div>
                                <span className="block text-gray-500 font-semibold">{t('Date')}</span>
                                <span className="font-bold text-gray-800">{new Date(viewedReturn.created_at).toLocaleString()}</span>
                            </div>
                            <div>
                                <span className="block text-gray-500 font-semibold">{t('Customer')}</span>
                                <span className="font-bold text-gray-800">{viewedReturn.customer_name || t('Walk-in')}</span>
                            </div>
                            <div>
                                <span className="block text-gray-500 font-semibold">{t('Total Refunded')}</span>
                                <span className="font-bold text-red-600">CFA{parseFloat(viewedReturn.total_refund).toFixed(2)}</span>
                            </div>
                        </div>

                        <div>
                            <h3 className="font-bold text-gray-800 mb-2">{t('Returned Items')}</h3>
                            {isViewLoading ? (
                                <div className="text-center py-8 text-gray-500 font-medium">Loading items...</div>
                            ) : viewedReturnItems.length > 0 ? (
                                <div className="border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="w-full overflow-x-auto max-w-full">
<table className="w-full text-left text-sm">
                                        <thead className="bg-gray-100 text-gray-600">
                                            <tr>
                                                <th className="p-3">{t('Product')}</th>
                                                <th className="p-3">{t('Barcode')}</th>
                                                <th className="p-3 text-center">{t('Unit Price')}</th>
                                                <th className="p-3 text-center">{t('Qty Returned')}</th>
                                                <th className="p-3 text-right">{t('Refund Value')}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {viewedReturnItems.map((item, idx) => (
                                                <tr key={idx} className="hover:bg-gray-50">
                                                    <td className="p-3">{item.product_name}</td>
                                                    <td className="p-3 font-mono text-gray-600">{item.barcode || '-'}</td>
                                                    <td className="p-3 text-center">CFA{parseFloat(item.unit_price || 0).toFixed(2)}</td>
                                                    <td className="p-3 text-center font-bold text-gray-800">{item.qty_refunded}</td>
                                                    <td className="p-3 text-right font-bold text-red-600">CFA{parseFloat(item.refund_amount || 0).toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
</div>
                                </div>
                            ) : (
                                <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200 text-gray-500">
                                    No item details found for this return. (It may be a legacy return without item records).
                                </div>
                            )}
                        </div>

                        <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100">
                            <span className="block text-blue-800 font-semibold mb-1">{t('Reason')}</span>
                            <p className="text-gray-700 italic">{viewedReturn.reason || 'No reason provided.'}</p>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ReturnManagement;
